import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { VoiceConversation } from "@elevenlabs/react";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import Spinner from "../components/spinner";
import VOICE, { describeVoiceError, VOICE_OFF_MESSAGE } from "../api/services/VOICE";

// Example prompts — same phrasing the mobile voice sheet shows.
const EXAMPLES = [
  "״קבע תור להדס שיין לציפורניים מחר ב-16:00״",
  "״מה יש לי ביומן היום?״",
  "״תבטל את התור של נועה מחר״",
  "״מתי יש לי חלון פנוי ביום שלישי?״",
];

// ── ElevenLabs conversation SDK ──────────────────────────────────────────────
// A live conversation runs over ElevenLabs' WebRTC transport (LiveKit under the
// hood). That handshake is NOT a plain RTCPeerConnection an app can build with
// browser APIs: the conversationToken the backend mints is only redeemable
// through their client SDK, which owns the signalling, the room join and the
// audio tracks.
//
// The class is `VoiceConversation` — there is no `Conversation` export in
// @elevenlabs/client v1.x. @elevenlabs/react re-exports the whole client, so we
// import from the one package the app depends on. `VoiceConversation` is the
// imperative API (static startSession → instance with endSession), which is what
// this screen wants; the useConversation() hook is the alternative but requires a
// <ConversationProvider> ancestor and re-renders on every status change.
//
// The resolver is kept as a genuine capability check rather than a stub: WebRTC
// needs a secure context, so on plain http (other than localhost) there is
// nothing to start, and the screen shows its "unavailable" state instead of
// opening a mic it cannot use.
function resolveConversationSdk() {
  if (typeof window === "undefined") return null;
  if (typeof VoiceConversation?.startSession !== "function") return null;
  // getUserMedia already requires a secure context; be explicit so the failure
  // is an honest message rather than a mid-handshake error.
  if (window.isSecureContext === false) return null;
  return VoiceConversation;
}

// Mic failures come back as DOMException names — map them to something a
// business owner can act on.
function micErrorMessage(err) {
  const name = (err && err.name) || "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "הגישה למיקרופון נדחתה. אפשרו גישה למיקרופון בהגדרות הדפדפן (סמל המנעול בשורת הכתובת) ונסו שוב.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "לא נמצא מיקרופון במחשב. חברו מיקרופון או אוזניות ונסו שוב.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "המיקרופון תפוס על ידי תוכנה אחרת. סגרו אותה ונסו שוב.";
  }
  return (err && err.message) || "לא ניתן לגשת למיקרופון.";
}

const VoiceAssistant = () => {
  const [loading, setLoading] = useState(true);
  // null while unknown; the whole toggle card hides if the endpoint isn't
  // deployed on this environment yet.
  const [status, setStatus] = useState(null);
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  const [saving, setSaving] = useState(false);

  // Session: 'idle' | 'starting' | 'live' | 'unavailable' | 'error'
  const [phase, setPhase] = useState("idle");
  const [failure, setFailure] = useState(null); // { kind, message }
  const [liveLine, setLiveLine] = useState("");
  const [micLevel, setMicLevel] = useState(0);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const sessionRef = useRef(null);
  const startingRef = useRef(false);

  // ── status ────────────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const data = await VOICE.get_assistant_status(); // get() returns the body
      setStatus(data && typeof data === "object" ? data : {});
      setStatusUnavailable(false);
    } catch (e) {
      const info = describeVoiceError(e);
      // A 403 is deployed, authoritative state: the platform master switch is
      // off for everyone. Adopt it as the status instead of hiding the card
      // behind a toast — otherwise the screen keeps offering a "התחלת שיחה"
      // button that cannot work and only fails after the mic prompt.
      if (info.kind === "platform_off") {
        setStatus({ platformMode: "off", enabled: false });
        setStatusUnavailable(false);
      } else if (info.kind === "not_deployed" || info.kind === "network") {
        // Committed-but-not-deployed → hide the card quietly, never render junk.
        setStatusUnavailable(true);
      } else {
        setStatusUnavailable(true);
        toast.error(info.message);
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchStatus();
      setLoading(false);
    })();
  }, [fetchStatus]);

  const platformMode = (status && status.platformMode) || "per_business";
  const platformOff = platformMode === "off";
  const platformForcedOn = platformMode === "all";
  const ownEnabled = !!(status && status.enabled);
  // Forced on/off by the platform → the switch mirrors the platform, not the
  // business flag, and can't be moved (same rule as the mobile settings screen).
  const switchOn = platformForcedOn ? true : platformOff ? false : ownEnabled;
  const switchLocked = platformOff || platformForcedOn || saving;

  const rate =
    status && typeof status.ratePerMinute === "number"
      ? status.ratePerMinute
      : null;

  const freeUntilLabel = (() => {
    const raw = status && status.freeUntil;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime()) || d.getTime() < Date.now()) return "";
    return d.toLocaleDateString("he-IL");
  })();

  const toggleAssistant = useCallback(
    async (next) => {
      if (switchLocked) return;
      const previous = ownEnabled;
      setSaving(true);
      setStatus((prev) => ({ ...(prev || {}), enabled: next })); // optimistic
      try {
        const res = await VOICE.set_assistant_enabled(next);
        const confirmed =
          typeof res?.data?.enabled === "boolean" ? res.data.enabled : next;
        setStatus((prev) => ({ ...(prev || {}), enabled: confirmed }));
        toast.success(
          confirmed ? "העוזר לאישור הגעה הופעל" : "העוזר לאישור הגעה כובה"
        );
      } catch (e) {
        setStatus((prev) => ({ ...(prev || {}), enabled: previous })); // revert
        const info = describeVoiceError(e);
        toast.error(info.message);
        // The platform switch may have been flipped off since we loaded —
        // re-read so the card stops offering a toggle that can't work.
        if (info.kind === "platform_off") fetchStatus();
      } finally {
        setSaving(false);
      }
    },
    [switchLocked, ownEnabled, fetchStatus]
  );

  // ── mic + session plumbing ────────────────────────────────────────────────
  const stopMeter = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch (_) {
        /* already closed */
      }
      audioCtxRef.current = null;
    }
    setMicLevel(0);
  }, []);

  const releaseMic = useCallback(() => {
    stopMeter();
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch (_) {
        /* nothing to stop */
      }
      streamRef.current = null;
    }
  }, [stopMeter]);

  const endSession = useCallback(() => {
    if (sessionRef.current) {
      try {
        // SEAM (teardown): whatever the SDK returns must expose an end call.
        const s = sessionRef.current;
        if (typeof s.endSession === "function") s.endSession();
        else if (typeof s.end === "function") s.end();
      } catch (_) {
        /* already closed */
      }
      sessionRef.current = null;
    }
    releaseMic();
    setLiveLine("");
  }, [releaseMic]);

  // Live level meter off the raw mic stream — pure Web Audio, no dependency.
  // Cosmetic only: any failure here must never break a session.
  const startMeter = useCallback((stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buffer = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buffer);
        let peak = 0;
        for (let i = 0; i < buffer.length; i += 1) {
          const v = Math.abs(buffer[i] - 128) / 128;
          if (v > peak) peak = v;
        }
        setMicLevel(peak);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (_) {
      /* meter is decoration — ignore */
    }
  }, []);

  // Unmount → never leave a hot mic or a live room behind.
  useEffect(() => endSession, [endSession]);

  const startSession = useCallback(async () => {
    if (startingRef.current || phase === "live") return;
    startingRef.current = true;
    setFailure(null);
    setLiveLine("");
    setPhase("starting");

    try {
      // 1) Mic first. It costs nothing, and asking for it before the token
      //    means a denied prompt doesn't burn one of the business's uses.
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setFailure({
          kind: "mic",
          message:
            "הדפדפן חוסם גישה למיקרופון. שיחה קולית דורשת חיבור מאובטח (https) ודפדפן מודרני.",
        });
        setPhase("error");
        return;
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr) {
        setFailure({ kind: "mic", message: micErrorMessage(micErr) });
        setPhase("error");
        return;
      }
      streamRef.current = stream;
      startMeter(stream);

      // 2) The SDK check comes BEFORE the token request on purpose: minting a
      //    conversation token consumes one of the business's free voice uses
      //    server-side, so we must not spend one on a session we can't open.
      const sdk = resolveConversationSdk();
      if (!sdk) {
        // Permission is verified, but nothing is going to listen — never keep
        // a hot mic open behind an explanatory notice.
        releaseMic();
        setPhase("unavailable");
        return;
      }

      // 3) Token. 403 = platform off, 402 = trial spent, 502 = ElevenLabs down.
      let token = "";
      let dynamicVars = {};
      try {
        const data = await VOICE.get_conversation_token();
        token = (data && data.conversationToken) || "";
        dynamicVars = (data && data.dynamicVars) || {};
        if (!token) {
          setFailure({
            kind: "service_down",
            message: "שירות הקול אינו זמין כרגע. נסו שוב בעוד רגע.",
          });
          setPhase("error");
          releaseMic();
          return;
        }
      } catch (e) {
        const info = describeVoiceError(e);
        setFailure(info);
        setPhase("error");
        releaseMic();
        if (info.kind === "platform_off") fetchStatus();
        return;
      }

      // 4) ── SEAM: the single call the ElevenLabs SDK owns ──────────────────
      //    Everything above (mic stream, token, dynamic variables) is ready;
      //    this hands them to the SDK, which does the WebRTC negotiation.
      try {
        sessionRef.current = await sdk.startSession({
          conversationToken: token,
          connectionType: "webrtc",
          dynamicVariables: dynamicVars,
          onConnect: () => setPhase("live"),
          onMessage: (payload) => {
            const text =
              typeof payload === "string" ? payload : payload && payload.message;
            const clean = text ? String(text).trim() : "";
            if (clean) setLiveLine(clean);
          },
          onDisconnect: () => {
            sessionRef.current = null;
            releaseMic();
            setPhase("idle");
            setLiveLine("");
          },
          onError: (err) => {
            setFailure({
              kind: "session",
              message:
                (typeof err === "string" ? err : err && err.message) ||
                "השיחה נותקה. נסו שוב.",
            });
            setPhase("error");
            releaseMic();
          },
        });
        setPhase("live");
      } catch (sdkErr) {
        setFailure({
          kind: "session",
          message:
            (sdkErr && sdkErr.message) || "לא ניתן לפתוח שיחה. נסו שוב בעוד רגע.",
        });
        setPhase("error");
        releaseMic();
      }
    } finally {
      startingRef.current = false;
    }
  }, [phase, startMeter, releaseMic, fetchStatus]);

  const cancelSession = useCallback(() => {
    endSession();
    setPhase("idle");
    setFailure(null);
  }, [endSession]);

  if (loading) {
    return (
      <Layout>
        <div style={S.container}>
          <div style={S.wrapper}>
            <div style={S.content}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const bars = Array.from({ length: 9 });

  return (
    <Layout>
      <div style={S.container}>
        <div style={S.wrapper}>
          <div style={S.content}>
            <h1 style={S.pageTitle}>עוזר קולי</h1>

            {/* ── explanation ── */}
            <div style={S.card}>
              <div style={S.cardTitleGroup}>
                <div style={S.icon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                    <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.93V21a1 1 0 1 1-2 0v-3.07A7 7 0 0 1 5 11a1 1 0 1 1 2 0 5 5 0 0 0 10 0Z" />
                  </svg>
                </div>
                <span style={S.cardTitle}>העוזר הקולי של העסק</span>
              </div>
              <p style={S.body}>
                העוזר הקולי מקבל תורים, בודק זמינות ביומן, מבטל ומזיז תורים — הכול
                בדיבור חופשי בעברית. הוא עובד מול היומן האמיתי שלכם, ולכן לא יציע
                שעה תפוסה ולא ימציא שירות שאינו קיים.
              </p>
              <p style={S.body}>
                בנוסף, העוזר יכול לחייג ללקוח שלא אישר את התור בתזכורת ולוודא
                הגעה — כך שתגיעו לבוקר עם יומן מאושר.
              </p>
            </div>

            {/* ── confirmation-call toggle (hidden when the endpoint isn't live) ── */}
            {!statusUnavailable && (
              <div style={S.card}>
                <div style={S.toggleRow}>
                  <span style={S.toggleLabel}>עוזר וירטואלי לאישור הגעה</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={switchOn}
                    aria-label="עוזר וירטואלי לאישור הגעה"
                    disabled={switchLocked}
                    onClick={() => toggleAssistant(!switchOn)}
                    style={{
                      ...S.switchTrack,
                      background: switchOn ? "#de1468" : "#e2e8f0",
                      cursor: switchLocked ? "not-allowed" : "pointer",
                      opacity: switchLocked ? 0.65 : 1,
                    }}
                  >
                    <span
                      style={{
                        ...S.switchKnob,
                        transform: switchOn
                          ? "translateX(-20px)"
                          : "translateX(0)",
                      }}
                    />
                  </button>
                </div>

                <p style={S.body}>
                  {platformOff
                    ? `${VOICE_OFF_MESSAGE} — הפיצ׳ר כובה על ידי המערכת, ולכן לא ניתן להפעיל אותו מכאן.`
                    : platformForcedOn
                    ? "הפיצ׳ר מופעל לכל העסקים על ידי המערכת."
                    : `אם הלקוח לא מאשר את התור בתזכורת, העוזר יחייג אליו לאישור.${
                        rate !== null
                          ? ` בחיוב נוסף של ₪${rate} לדקת שיחה.`
                          : ""
                      }`}
                </p>

                {!platformOff && !!freeUntilLabel && (
                  <div style={S.freeLine}>
                    <span style={S.freeDot} />
                    <span>ללא חיוב עד {freeUntilLabel}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── live session ── */}
            <div style={S.card}>
              <span style={S.cardTitle}>שיחה עם העוזר הקולי</span>

              {phase === "idle" && (
                <>
                  <p style={S.body}>
                    לחצו על ״התחלת שיחה״, אשרו גישה למיקרופון ודברו — כמו שיחת
                    טלפון רגילה.
                  </p>
                  <div style={S.examples}>
                    {EXAMPLES.map((ex) => (
                      <span key={ex} style={S.example}>
                        {ex}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    style={{
                      ...S.primaryBtn,
                      ...(platformOff ? S.btnDisabled : null),
                    }}
                    onClick={startSession}
                    disabled={platformOff}
                  >
                    התחלת שיחה
                  </button>
                  {platformOff && (
                    <p style={S.mutedNote}>
                      {VOICE_OFF_MESSAGE} — לא ניתן לפתוח שיחה כרגע.
                    </p>
                  )}
                </>
              )}

              {phase === "starting" && (
                <div style={S.centerBox}>
                  <Spinner />
                  <p style={S.centerText}>
                    מתחבר… אשרו את בקשת הגישה למיקרופון בדפדפן.
                  </p>
                  <button type="button" style={S.ghostBtn} onClick={cancelSession}>
                    ביטול
                  </button>
                </div>
              )}

              {phase === "live" && (
                <div style={S.centerBox}>
                  <div style={S.liveRow}>
                    <span style={S.liveDot} />
                    <span style={S.liveText}>שיחה פעילה</span>
                  </div>
                  <div style={S.waveRow}>
                    {bars.map((_, i) => {
                      const spread = 1 - Math.abs(i - 4) / 6;
                      const h = 8 + micLevel * 46 * spread;
                      return (
                        <span
                          key={`bar-${i}`}
                          style={{ ...S.waveBar, height: `${h}px` }}
                        />
                      );
                    })}
                  </div>
                  {!!liveLine && <p style={S.liveLine}>{liveLine}</p>}
                  <button type="button" style={S.dangerBtn} onClick={cancelSession}>
                    סיום שיחה
                  </button>
                </div>
              )}

              {/* Honest degradation — the browser side works, the SDK is missing.
                  No token was requested, so no free use was spent. */}
              {phase === "unavailable" && (
                <div style={S.noticeBox}>
                  <span style={S.noticeTitle}>
                    המיקרופון מוכן — אך מודול השיחה החי אינו מותקן בדשבורד
                  </span>
                  <p style={S.noticeBody}>
                    השיחה עם העוזר הקולי רצה על גבי WebRTC של ElevenLabs, וחיבור
                    כזה אפשרי רק דרך ה-SDK הרשמי שלהם. החבילה אינה מותקנת בדשבורד
                    כרגע, ולכן לא ניתן לפתוח שיחה מהמסך הזה. כל שאר החלקים כבר
                    עובדים — ההרשאה למיקרופון, ההגדרות והחיבור לשרת — וברגע
                    שהחבילה תותקן השיחה תיפתח מכאן ללא שינוי נוסף.
                  </p>
                  <p style={S.noticeBody}>
                    בינתיים אפשר לדבר עם העוזר הקולי מאפליקציית המובייל של תור.
                  </p>
                  <p style={S.mutedNote}>
                    לא נוצל שימוש מהמכסה שלכם — שיחה לא נפתחה.
                  </p>
                  <button type="button" style={S.ghostBtn} onClick={cancelSession}>
                    סגירה
                  </button>
                </div>
              )}

              {phase === "error" && (
                <div style={S.errorBox}>
                  <span style={S.errorTitle}>
                    {failure && failure.kind === "locked"
                      ? "העוזר הקולי נעול"
                      : "לא ניתן להתחיל שיחה"}
                  </span>
                  <p style={S.errorBody}>
                    {(failure && failure.message) || "שגיאה בשירות הקולי"}
                  </p>
                  {failure && failure.kind === "locked" && (
                    <p style={S.errorBody}>
                      ניצלתם את השימושים החינמיים בעוזר הקולי. ניתן להוסיף את
                      העוזר הקולי למנוי מאפליקציית המובייל ולהמשיך לנהל את היומן
                      בדיבור.
                    </p>
                  )}
                  <div style={S.errorActions}>
                    {(!failure || failure.kind !== "locked") && (
                      <button
                        type="button"
                        style={{
                          ...S.primaryBtn,
                          ...(platformOff ? S.btnDisabled : null),
                          marginTop: 0,
                        }}
                        onClick={startSession}
                        disabled={platformOff}
                      >
                        נסו שוב
                      </button>
                    )}
                    <button
                      type="button"
                      style={{ ...S.ghostBtn, marginTop: 0 }}
                      onClick={cancelSession}
                    >
                      סגירה
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// ── styles ───────────────────────────────────────────────────────────────────
// Inline, on purpose: this screen owns no .scss module (the styles folder is
// off-limits for this change), and the values below mirror the shared card /
// button language used by the other dashboard screens.
const S = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    marginTop: 40,
    padding: "0 0 100px 0",
    position: "relative",
    direction: "rtl",
  },
  wrapper: { width: "100%", maxWidth: 900 },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 20,
    padding: "0 24px 24px 24px",
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 4,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    padding: 24,
    border: "1px solid #e2e8f0",
  },
  cardTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  icon: {
    width: 40,
    height: 40,
    background: "#de1468",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1e293b",
    display: "block",
  },
  body: {
    fontSize: 14,
    lineHeight: 1.75,
    color: "#64748b",
    margin: "8px 0 0",
  },
  mutedNote: {
    fontSize: 13,
    lineHeight: 1.7,
    color: "#94a3b8",
    margin: "10px 0 0",
  },
  // toggle
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  toggleLabel: { fontSize: 16, fontWeight: 700, color: "#1e293b" },
  switchTrack: {
    position: "relative",
    width: 48,
    height: 28,
    borderRadius: 28,
    border: "none",
    padding: 0,
    flexShrink: 0,
    transition: "background 0.2s",
  },
  switchKnob: {
    position: "absolute",
    top: 3,
    right: 3,
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
    transition: "transform 0.2s",
  },
  freeLine: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 14,
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 12,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: "#16a34a",
  },
  freeDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#16a34a",
    flexShrink: 0,
  },
  // examples
  examples: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    margin: "16px 0 4px",
  },
  example: {
    background: "#f5f0fb",
    color: "#44207a",
    borderRadius: 15,
    padding: "8px 14px",
    fontSize: 13,
    lineHeight: 1.6,
  },
  // buttons
  primaryBtn: {
    marginTop: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 200,
    height: 46,
    padding: "0 24px",
    border: "none",
    borderRadius: 10,
    background: "#de1468",
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  dangerBtn: {
    marginTop: 18,
    minWidth: 180,
    height: 44,
    padding: "0 20px",
    border: "none",
    borderRadius: 10,
    background: "#dc2626",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  ghostBtn: {
    marginTop: 16,
    height: 44,
    padding: "0 20px",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    background: "#fff",
    color: "#64748b",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnDisabled: { opacity: 0.55, cursor: "not-allowed" },
  // live session
  centerBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "18px 0 4px",
  },
  centerText: {
    fontSize: 15,
    fontWeight: 600,
    color: "#1e293b",
    margin: "14px 0 0",
    textAlign: "center",
  },
  liveRow: { display: "flex", alignItems: "center", gap: 8 },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#16a34a",
    flexShrink: 0,
  },
  liveText: { fontSize: 14, fontWeight: 700, color: "#1e293b" },
  waveRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    height: 60,
    marginTop: 16,
  },
  waveBar: {
    display: "block",
    width: 6,
    minHeight: 8,
    borderRadius: 3,
    background: "#de1468",
    transition: "height 0.08s linear",
  },
  liveLine: {
    fontSize: 13,
    lineHeight: 1.7,
    color: "#64748b",
    textAlign: "center",
    margin: "14px 0 0",
    maxWidth: 520,
  },
  // notices
  noticeBox: {
    marginTop: 14,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 12,
    padding: "16px 18px",
  },
  noticeTitle: {
    display: "block",
    fontSize: 15,
    fontWeight: 700,
    color: "#c2410c",
    marginBottom: 6,
  },
  noticeBody: {
    fontSize: 13.5,
    lineHeight: 1.8,
    color: "#9a3412",
    margin: "8px 0 0",
  },
  errorBox: {
    marginTop: 14,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    padding: "16px 18px",
  },
  errorTitle: {
    display: "block",
    fontSize: 15,
    fontWeight: 700,
    color: "#b91c1c",
    marginBottom: 6,
  },
  errorBody: {
    fontSize: 13.5,
    lineHeight: 1.8,
    color: "#7f1d1d",
    margin: "8px 0 0",
  },
  errorActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 16,
  },
};

export default VoiceAssistant;
